import { SlComponent, SlPaginatedPage } from '@varo/graphql-schema-types';
import { useRouter } from 'expo-router';
import { getMergedSmartLedgerComponents } from 'features/accounts/AccountDetails/AccountDetails.helpers';
import { useCallback, useEffect, useRef, useState } from 'react';
import SmartLedgerComponents from 'services/graphql/queries/SmartLedger';
import { useDefaultErrorStrings } from 'shared/hooks/errorFormatters/useDefaultError/useDefaultErrorStrings';
import { ErrorScreenType } from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import { useQuery } from 'urql';

const useGetRecentTransaction = (
  accountId: string,
  fetchTransactions: boolean,
) => {
  const router = useRouter();
  const isFetchingNextPage = useRef(fetchTransactions);
  const [pagingToken, setPagingToken] = useState<string | null>(null);

  const [smartLedgerComponents, setSmartLedgerComponents] = useState<
    SlComponent[] | null
  >(null);

  const [{ data, fetching, error }, refetchSmartLedger] = useQuery<{
    smartLedgerComponents: SlPaginatedPage;
  }>({
    query: SmartLedgerComponents,
    variables: {
      accountId,
      pagingToken,
    },
    pause: !accountId || !fetchTransactions || Boolean(smartLedgerComponents),
    requestPolicy: 'network-only',
  });

  const fetchNextPage = useCallback(() => {
    if (!fetching && !isFetchingNextPage.current && pagingToken) {
      isFetchingNextPage.current = true;
      refetchSmartLedger();
    }
  }, [fetching, refetchSmartLedger, pagingToken]);

  useEffect(() => {
    // this should only ever run when `data` changes and is `isFetchingNextPage.current` is true
    //
    // this is true since `setSmartLedgerComponents` is only set in here and
    // isFetchingNextPage.current is set to false in this block.
    if (isFetchingNextPage.current && data) {
      const { children, pagingToken } = data?.smartLedgerComponents ?? {};

      isFetchingNextPage.current = false;
      setPagingToken(pagingToken ?? null);
      setSmartLedgerComponents((prevSmartLedgerComponents) =>
        getMergedSmartLedgerComponents(
          prevSmartLedgerComponents ?? [],
          children,
        ),
      );
    }
  }, [data]);

  const { defaultTitle, defaultMessage, cancelButtonTitle } =
    useDefaultErrorStrings();
  const cancel = () => {
    router.back();
  };

  const props = {
    label: cancelButtonTitle,
    onPress: cancel,
  };

  const errorModel = error?.message
    ? {
        screenType: ErrorScreenType.Error,
        messageHeadline: defaultTitle,
        messageBody: error.message ?? defaultMessage,
        props,
      }
    : null;

  return {
    transactions: smartLedgerComponents,
    transactionError: errorModel,
    loadingTransactions: smartLedgerComponents ? false : fetching,
    loadingMoreData: Boolean(
      smartLedgerComponents && isFetchingNextPage.current,
    ),
    fetchData: fetchNextPage,
  };
};

export default useGetRecentTransaction;
